import { FALLBACK_LOCALE, MARKETS, findMarket } from './markets';

/**
 * A locale is `<market>-<language>` — `at-de`, `hu-en`. It is the first path
 * segment of every application URL (CON-002).
 *
 * Note the segment order is MARKET FIRST, which is the reverse of a BCP-47 tag
 * (`de-AT`). That is deliberate: the market is the primary content axis (which
 * plants, which hardiness zone, which planting calendar) and the language is the
 * secondary one, so the URL reads outermost-scope-first and market landing pages
 * group naturally. `toBcp47` below is the bridge for anything that needs the
 * standard tag — `<html lang>`, `hreflang`, `Intl`.
 */

const LOCALE_PATTERN = /^([a-z]{2})-([a-z]{2})$/u;

export function formatLocale(marketCode, languageCode) {
    return `${marketCode}-${languageCode}`;
}

export function parseLocale(localeCode) {
    const match = LOCALE_PATTERN.exec(String(localeCode || '').toLowerCase());

    if (!match) {
        return null;
    }

    const [, marketCode, languageCode] = match;
    const market = findMarket(marketCode);

    if (!market || !market.languages.includes(languageCode)) {
        return null;
    }

    return { localeCode: formatLocale(marketCode, languageCode), marketCode, languageCode };
}

export function isSupportedLocale(localeCode) {
    return parseLocale(localeCode) !== null;
}

/** Every shippable locale, in market order — drives hreflang and the language switcher. */
export function listSupportedLocales() {
    return MARKETS.flatMap((market) => (
        market.languages.map((languageCode) => formatLocale(market.code, languageCode))
    ));
}

/** `at-de` -> `de-AT`, for `<html lang>`, `hreflang` and `Intl`. */
export function toBcp47(localeCode) {
    const parsed = parseLocale(localeCode);

    if (!parsed) {
        return null;
    }

    return `${parsed.languageCode}-${parsed.marketCode.toUpperCase()}`;
}

/**
 * Split a pathname into its locale segment and the route beneath it.
 * An unprefixed or unsupported-prefix path yields `localeCode: null` so the
 * caller can decide whether to negotiate-and-redirect or render a 404.
 */
export function splitLocalePath(pathname) {
    const [, firstSegment = '', ...rest] = String(pathname || '/').split('/');
    const parsed = parseLocale(firstSegment);

    if (!parsed) {
        return { localeCode: null, routePath: String(pathname || '/') || '/' };
    }

    return { localeCode: parsed.localeCode, routePath: `/${rest.join('/')}` };
}

/**
 * Drop a first segment that has the SHAPE of a locale, whether or not we ship it.
 *
 * This is what makes `/fr-fr/plants` land on `/at-en/plants` rather than
 * `/at-en` — a visitor following a link for a market we have not launched keeps
 * the screen they were promised. `splitLocalePath` cannot do this job: it only
 * recognises supported locales, so to it `fr-fr` is an ordinary path segment.
 */
const LOCALE_SHAPED_SEGMENT = /^[a-z]{2}-[a-z]{2}$/u;

export function stripLocaleLikePrefix(pathname) {
    const normalizedPath = String(pathname || '/') || '/';
    const [, firstSegment = '', ...rest] = normalizedPath.split('/');

    if (!LOCALE_SHAPED_SEGMENT.test(firstSegment.toLowerCase())) {
        return normalizedPath;
    }

    return `/${rest.join('/')}`;
}

/** Move a path to another locale, keeping the route beneath it — the language switcher. */
export function withLocale(pathname, localeCode) {
    const { routePath } = splitLocalePath(pathname);
    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');

    return `/${localeCode}${normalizedRoute}`;
}

export { FALLBACK_LOCALE };
